import { CaptureUpdateAction } from "@excalidraw/excalidraw";
import { newElementWith } from "@excalidraw/element";
import throttle from "lodash.throttle";

import type { UserIdleState } from "@excalidraw/common";
import type { OrderedExcalidrawElement } from "@excalidraw/element/types";
import type {
  OnUserFollowedPayload,
  SocketId,
} from "@excalidraw/excalidraw/types";

import { FILE_UPLOAD_TIMEOUT, WS_SUBTYPES } from "../app_constants";
import { isSyncableElement } from "../data";

import type {
  SocketUpdateData,
  SocketUpdateDataSource,
  SyncableExcalidrawElement,
} from "../data";
import type { TCollabClass } from "./Collab";

// crypto.randomUUID isn't available in every environment (e.g. some test
// crypto mocks); fall back to a random string so we always have a client id.
const generateSocketId = (): SocketId =>
  (typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2)) as SocketId;

// Transport is now a native WebSocket to the DiagramRoom Durable Object
// (see server/src/rooms/DiagramRoom.ts). Messages are plaintext JSON of the
// shape { type, payload } — the same SocketUpdateData vocabulary the editor
// already used. The room is identified by the connection URL, so there is no
// per-message roomId, and no client-side encryption (the authoritative scene
// lives server-side; security is TLS + encryption at rest).
class Portal {
  collab: TCollabClass;
  ws: WebSocket | null = null;
  // client-generated id standing in for the old socket.io socket.id; used to
  // attribute our presence (cursor / idle) messages to other peers.
  socketId: SocketId = generateSocketId();
  socketInitialized: boolean = false; // don't emit updates until initialized
  roomId: string | null = null;
  // retained purely to encrypt image files in storage (Phase 2 moves these to
  // R2); it is no longer used for real-time message encryption.
  roomKey: string | null = null;
  broadcastedElementVersions: Map<string, number> = new Map();

  constructor(collab: TCollabClass) {
    this.collab = collab;
  }

  open(ws: WebSocket, id: string, key: string) {
    this.ws = ws;
    this.roomId = id;
    this.roomKey = key;
    this.socketId = generateSocketId();
    // The DiagramRoom sends SCENE_INIT on connect and tracks membership itself,
    // so the old socket.io handshake (init-room / join-room / new-user /
    // room-user-change) is no longer needed here.
    return ws;
  }

  close() {
    if (!this.ws) {
      return;
    }
    this.queueFileUpload.flush();
    this.ws.close();
    this.ws = null;
    this.roomId = null;
    this.roomKey = null;
    this.socketInitialized = false;
    this.broadcastedElementVersions = new Map();
  }

  isOpen() {
    return !!(
      this.socketInitialized &&
      this.ws &&
      this.ws.readyState === WebSocket.OPEN &&
      this.roomId
    );
  }

  async _broadcastSocketData(data: SocketUpdateData) {
    if (this.isOpen()) {
      this.ws?.send(JSON.stringify(data));
    }
  }

  queueFileUpload = throttle(async () => {
    try {
      await this.collab.fileManager.saveFiles({
        elements: this.collab.excalidrawAPI.getSceneElementsIncludingDeleted(),
        files: this.collab.excalidrawAPI.getFiles(),
      });
    } catch (error: any) {
      if (error.name !== "AbortError") {
        this.collab.excalidrawAPI.updateScene({
          appState: {
            errorMessage: error.message,
          },
        });
      }
    }

    let isChanged = false;
    const newElements = this.collab.excalidrawAPI
      .getSceneElementsIncludingDeleted()
      .map((element) => {
        if (this.collab.fileManager.shouldUpdateImageElementStatus(element)) {
          isChanged = true;
          // this will signal collaborators to pull image data from server
          // (using mutation instead of newElementWith otherwise it'd break
          // in-progress dragging)
          return newElementWith(element, { status: "saved" });
        }
        return element;
      });

    if (isChanged) {
      this.collab.excalidrawAPI.updateScene({
        elements: newElements,
        captureUpdate: CaptureUpdateAction.NEVER,
      });
    }
  }, FILE_UPLOAD_TIMEOUT);

  broadcastScene = async (
    updateType: WS_SUBTYPES.INIT | WS_SUBTYPES.UPDATE,
    elements: readonly OrderedExcalidrawElement[],
    syncAll: boolean,
  ) => {
    if (updateType === WS_SUBTYPES.INIT && !syncAll) {
      throw new Error("syncAll must be true when sending SCENE.INIT");
    }

    // sync out only the elements we think we need to to save bandwidth.
    // periodically we'll resync the whole thing to make sure no one diverges
    // due to a dropped message (server goes down etc).
    const syncableElements = elements.reduce((acc, element) => {
      if (
        (syncAll ||
          !this.broadcastedElementVersions.has(element.id) ||
          element.version > this.broadcastedElementVersions.get(element.id)!) &&
        isSyncableElement(element)
      ) {
        acc.push(element);
      }
      return acc;
    }, [] as SyncableExcalidrawElement[]);

    const data: SocketUpdateDataSource[typeof updateType] = {
      type: updateType,
      payload: {
        elements: syncableElements,
      },
    };

    for (const syncableElement of syncableElements) {
      this.broadcastedElementVersions.set(
        syncableElement.id,
        syncableElement.version,
      );
    }

    this.queueFileUpload();

    await this._broadcastSocketData(data as SocketUpdateData);
  };

  broadcastIdleChange = (userState: UserIdleState) => {
    if (this.ws) {
      const data: SocketUpdateDataSource["IDLE_STATUS"] = {
        type: WS_SUBTYPES.IDLE_STATUS,
        payload: {
          socketId: this.socketId,
          userState,
          username: this.collab.state.username,
        },
      };
      return this._broadcastSocketData(data as SocketUpdateData);
    }
  };

  broadcastMouseLocation = (payload: {
    pointer: SocketUpdateDataSource["MOUSE_LOCATION"]["payload"]["pointer"];
    button: SocketUpdateDataSource["MOUSE_LOCATION"]["payload"]["button"];
  }) => {
    if (this.ws) {
      const data: SocketUpdateDataSource["MOUSE_LOCATION"] = {
        type: WS_SUBTYPES.MOUSE_LOCATION,
        payload: {
          socketId: this.socketId,
          pointer: payload.pointer,
          button: payload.button || "up",
          selectedElementIds:
            this.collab.excalidrawAPI.getAppState().selectedElementIds,
          username: this.collab.state.username,
        },
      };

      return this._broadcastSocketData(data as SocketUpdateData);
    }
  };

  broadcastVisibleSceneBounds = (payload: {
    sceneBounds: SocketUpdateDataSource["USER_VISIBLE_SCENE_BOUNDS"]["payload"]["sceneBounds"];
  }) => {
    if (this.ws) {
      const data: SocketUpdateDataSource["USER_VISIBLE_SCENE_BOUNDS"] = {
        type: WS_SUBTYPES.USER_VISIBLE_SCENE_BOUNDS,
        payload: {
          socketId: this.socketId,
          username: this.collab.state.username,
          sceneBounds: payload.sceneBounds,
        },
      };

      return this._broadcastSocketData(data as SocketUpdateData);
    }
  };

  // Follow-mode is relayed like any other presence message; the DiagramRoom
  // fans it out to peers. (Roster-scoped following is refined in a later phase.)
  broadcastUserFollowed = (_payload: OnUserFollowedPayload) => {
    // no-op until the DiagramRoom tracks a presence roster (Phase 3+)
  };
}

export default Portal;
